/**
 * @file Outils communs des constructeurs de cibles par theme : collecte de primitives colorees (Shape), types de prototypes, couleurs
 *       communes et surface polaire irreguliere. Les constructeurs eux-memes sont dans TargetFactory.ts (THEME_BUILDERS) et HubThemes.ts.
 */
import { BufferGeometry, Float32BufferAttribute } from 'three';
import type { TargetPalette } from '@config/types';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import { MergeBuilder, mixHex } from './MergeBuilder';
import type { PartOptions } from './MergeBuilder';

/** Une piece du prototype : geometrie fusionnee (mergeGeometries de primitives transformees) + couleur. */
export interface ProtoPart {
  geometry: BufferGeometry;
  color: string;
  emissive?: string;
  /** Piece de couronne (arbres geants) : rendue a part, elle devient translucide quand un chat passe dessous. */
  canopy?: boolean;
}

export interface TargetProto {
  key: string;
  parts: ProtoPart[];
  /** Hauteur (placement des barres de vie / textes flottants). */
  height: number;
  /** Rayon visuel (ombre au sol, effets). */
  radius: number;
  /** Arbres geants : hauteur du centre de la couronne (unites du prototype, echelle appliquee). */
  canopyMid?: number;
}

/** Contexte d'un constructeur : variante (graine de forme deterministe), palette, bibliotheque de primitives. */
export interface ThemeBuildContext {
  palette: TargetPalette;
  variant: number;
  /** PRNG deterministe derive de (defId, variant) : la meme variante produit toujours la meme forme. */
  rand: () => number;
  geo: GeometryLibrary;
}

export type ThemeBuilder = (c: ThemeBuildContext) => Omit<TargetProto, 'key'>;

export const TRUNK = '#8f6a45';
export const DARK = '#2b2017';
export const WHITE = '#ffffff';

/** Collecte de primitives colorees ; une ProtoPart (geometrie fusionnee) par couple (couleur, emissive). */
export class Shape {
  private readonly builder = new MergeBuilder();

  add(geometry: BufferGeometry, color: string, o: PartOptions = {}, emissive?: string): this {
    this.builder.add(geometry, color, { ...o, group: `${color}|${emissive ?? ''}` });
    return this;
  }

  /** Piece de couronne (voir ProtoPart.canopy). */
  crown(geometry: BufferGeometry, color: string, o: PartOptions = {}): this {
    this.builder.add(geometry, color, { ...o, group: `${color}||c` });
    return this;
  }

  finish(): Omit<TargetProto, 'key'> {
    const parts: ProtoPart[] = [];
    let height = 0;
    let radius = 0;
    let cMin = Infinity;
    let cMax = -Infinity;
    for (const group of this.builder.groups()) {
      const geometry = this.builder.fill(group);
      if (!geometry) continue;
      geometry.deleteAttribute('color');
      const [color = WHITE, emissive = '', flag = ''] = group.split('|');
      const part: ProtoPart = { geometry, color };
      if (emissive) part.emissive = emissive;
      if (flag === 'c') part.canopy = true;
      parts.push(part);
      const bb = geometry.boundingBox;
      if (bb && part.canopy) {
        cMin = Math.min(cMin, bb.min.y);
        cMax = Math.max(cMax, bb.max.y);
      }
      if (bb) {
        height = Math.max(height, bb.max.y);
        radius = Math.max(radius, Math.abs(bb.min.x), Math.abs(bb.max.x), Math.abs(bb.min.z), Math.abs(bb.max.z));
      }
    }
    return cMax > cMin ? { parts, height, radius, canopyMid: (cMin + cMax) / 2 } : { parts, height, radius };
  }
}

export const TAU = Math.PI * 2;
/** Couleurs communes des lieux du hub (hors palette). */
export const LEAF = '#7fd18c';
export const LEAF_DARK = '#5fb877';
export const PINK = '#ff9ec7';
export const YELLOW = '#ffd45e';
export const GLASS = '#bfe8ff';
export const BRICK = '#c9786a';

/** Point d'un profil polaire : [rayon, hauteur]. */
export type PolarPoint = readonly [number, number];

/**
 * Bande de surface entre deux profils polaires irreguliers (rayon et hauteur en fonction de l'angle) : sert aux plans d'eau, rives et
 * margelles. Face dessus vers +Y, normales calculees ; un profil interieur de rayon 0 donne un disque plein.
 */
export const polarBand = (segments: number, inner: (t: number) => PolarPoint, outer: (t: number) => PolarPoint): BufferGeometry => {
  const pos: number[] = [];
  const at = (t: number, [r, y]: PolarPoint): void => {
    pos.push(Math.cos(t) * r, y, Math.sin(t) * r);
  };
  for (let i = 0; i < segments; i++) {
    const t0 = (i / segments) * TAU;
    const t1 = ((i + 1) / segments) * TAU;
    const a = inner(t0);
    const b = outer(t0);
    const c = inner(t1);
    const d = outer(t1);
    at(t0, a);
    at(t1, c);
    at(t0, b);
    at(t0, b);
    at(t1, c);
    at(t1, d);
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
};

export const pick = <T>(arr: readonly T[], rand: () => number): T => arr[Math.floor(rand() * arr.length) % arr.length] as T;


/** Maison : hauteur du soubassement et des murs (unites du constructeur) et position de la cheminee selon la variante (3 formes de toit). */
export const HOUSE_FOUNDATION = 0.14;
export const HOUSE_WALL = 1.15;
export const houseChimney = (variant: number): { x: number; z: number; y: number; h: number } => {
  const top = HOUSE_FOUNDATION + HOUSE_WALL;
  const v = ((Math.floor(variant) % 3) + 3) % 3;
  if (v === 0) return { x: 0.6, z: -0.5, y: top + 0.42, h: 0.8 };
  if (v === 1) return { x: 0.5, z: -0.4, y: top + 0.5, h: 0.8 };
  return { x: 0.42, z: -0.3, y: top + 0.62, h: 0.9 };
};

/** Couleurs d'une maison selon la variante (0 toit pyramide, 1 pignon bleute, 2 tourelle verte) : murs, toit, soubassement. Partage avec fx/HouseCollapse. */
export const houseColors = (p: TargetPalette, variant: number): { wall: string; roof: string; base: string } => {
  const v = ((Math.floor(variant) % 3) + 3) % 3;
  return {
    wall: v === 1 ? mixHex(p.main, '#d8f0ff', 0.5) : v === 2 ? mixHex(p.main, '#ffd6e6', 0.45) : p.main,
    roof: v === 1 ? mixHex(p.secondary, '#5f9bff', 0.85) : v === 2 ? mixHex(p.secondary, '#6fd08c', 0.85) : p.secondary,
    base: mixHex(p.main, DARK, 0.18),
  };
};
